// Conversas do Inbox na aba "WhatsApp" da negociação.
//
// Tudo passa pela edge function `crm-conversas-negociacao`: ela decide quem vê
// (adm, gerente e o responsável pela negociação) e lê o Inbox com service_role.
// Ler `inbox_messages` direto daqui não funcionaria — a RLS do Inbox só libera
// membro do módulo, e o responsável pela negociação quase nunca é.

import { supabase } from '../../../../src/lib/supabase';
import type { Mensagem } from '../../../Omnichannel/Inbox/tipos';

export type OrigemConversa = 'whatsapp' | 'whatsapp_financeiro' | 'vinculo_manual';

export interface ConversaDaNegociacao {
  id: string;
  contato_nome: string | null;
  contato_telefone: string | null;
  conexao_nome: string;
  conexao_status: string;
  /** Tipo do canal e o que ele suporta (o compositor decide por aqui). */
  canal?: string;
  conexao_capabilities?: Record<string, unknown>;
  ultima_mensagem_em: string | null;
  origem: OrigemConversa;
  outras_negociacoes: number;
}

/** Última análise da IA da conversa (#6313). */
export interface AnaliseResumo {
  dia: string;
  comercial: boolean;
  probabilidade: number | null;
  temperatura: string | null;
  etapa: string | null;
  proximo_passo: string | null;
  resumo: string | null;
  alertas: number;
}

export interface ConexaoForaDoAr {
  nome: string;
  ultima_mensagem_em: string | null;
}

export type RespostaConversas =
  | { permitido: false }
  | {
      permitido: true;
      numeros: Array<{ campo: 'whatsapp' | 'whatsapp_financeiro'; telefone: string }>;
      conversas: ConversaDaNegociacao[];
      conversaId: string | null;
      mensagens: Mensagem[];
      temMaisAntigas: boolean;
      nomes: Record<string, string>;
      conexoesForaDoAr: ConexaoForaDoAr[];
      analises: Record<string, AnaliseResumo>;
    };

export async function carregarConversasDaNegociacao(params: {
  negociacaoId: number;
  conversationId?: string | null;
  anteriorA?: string | null;
}): Promise<RespostaConversas> {
  const { data, error } = await supabase.functions.invoke('crm-conversas-negociacao', { body: params });
  if (error) {
    // O SDK devolve só "non-2xx status code"; o motivo está no corpo.
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || 'Não foi possível carregar as conversas.');
  }
  if (!data?.ok) throw new Error(data?.error || 'Não foi possível carregar as conversas.');
  if (!data.permitido) return { permitido: false };

  return {
    permitido: true,
    numeros: data.numeros ?? [],
    conversas: data.conversas ?? [],
    conversaId: data.conversaId ?? null,
    // O balão do Inbox também mostra e-mail; aqui só vem WhatsApp, então os
    // campos de e-mail chegam vazios.
    mensagens: (data.mensagens ?? []).map((m: any) => ({
      subject: null,
      body_html: null,
      recipients: null,
      ...m,
    })) as Mensagem[],
    temMaisAntigas: !!data.temMaisAntigas,
    nomes: data.nomes ?? {},
    conexoesForaDoAr: data.conexoesForaDoAr ?? [],
    analises: data.analises ?? {},
  };
}
